import { Injectable, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from './auth.service';
import { SubscriptionService } from './subscription.service';
import { SelectedsystemService } from './selectedsystem.service';
import { DeviceService } from './device.service';
import { AppConfigPayload } from '../types/general.types';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SelectedContentService } from './selectedcontent.service';
import { SelectedSiteService } from './selectedsite.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SessionService {

  private socket!: Socket;
  private cooldownActive = false;
  private timer: any;
  private _config$ = new BehaviorSubject<AppConfigPayload | null>(null);
  sessionId: string = "";

  constructor(private ngZone: NgZone, private authService: AuthService, private subscriptionService: SubscriptionService, private selectedsystemService: SelectedsystemService, private deviceService: DeviceService, private selectedSiteService: SelectedSiteService, private selectedContentService: SelectedContentService, private msalAuthService: MsalService) {
    this.ngZone.runOutsideAngular(() => {
      //mousemove
      const activityEvents = ['click', 'keydown', 'scroll'];
      activityEvents.forEach(event =>
        document.addEventListener(event, this.handleClick.bind(this), { passive: true })
      );
    });
  }

  private getSocketUrl(): string {
    return window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "");
  }

  private async getSocketAccessToken(): Promise<string> {
    const account = this.msalAuthService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No active MSAL account found');
    }

    const response = await firstValueFrom(
      this.msalAuthService.acquireTokenSilent({
        scopes: [`api://${environment.backendId}/api.read.all`],
        account,
      })
    );

    return response.accessToken;
  }

  private async buildSocketAuthPayload(
    device: string,
    country: string,
    department: string,
    type: "device" | "site",
    serial: string,
    material: string,
    siteId: string,
    siteName: string,
  ): Promise<{ accessToken: string; tracking: Record<string, unknown> }> {
    const accessToken = await this.getSocketAccessToken();

    return {
      accessToken,
      tracking: {
        device,
        country,
        department,
        type,
        serial,
        material,
        siteId,
        siteName,
      },
    };
  }

  private registerSocketHandlers(): void {
    this.socket.on('connect', () => {
      console.log('✅ Connected with ID:', this.socket.id);
      this.sessionId = this.socket.id;
    });

    this.socket.on('disconnect', () => {
      console.log('❌ Disconnected');
      this.sessionId = "";
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection failed:', error?.message ?? error);
    });

    this.socket.on('sb-message', (data) => {

      const current = this.subscriptionService.$notifications.getValue();
      const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

      let message = {
        id: crypto.randomUUID(),
        type: "alert",
        title: data["body"]["subject"],
        message: data["body"]["data"]["alert_message"],
        timestamp: data["body"]["data"]["created_at"],
        read: false
      }

      this.subscriptionService.$notifications.next([...current, message]);
      this.subscriptionService.$notificationsCenter.next([...currentCenter, message]);
    });
  }

  private async reconnectSocket(): Promise<void> {
    if (!this.socket || this.selectedContentService.selection.kind === 'none') {
      return;
    }

    const selectionKind = this.selectedContentService.selection.kind;

    try {
      this.socket.once('connect', () => {
        this.socket.emit('checkActivity');
      });

      this.socket.auth = await this.buildSocketAuthPayload(
        this.deviceService.getDeviceType(),
        this.authService.userData.basic["country"],
        this.authService.userData.basic["department"],
        selectionKind,
        this.selectedContentService.currentSystem?.SerialNumber ?? null,
        this.selectedContentService.currentSystem?.MaterialNumber ?? null,
        this.selectedContentService.currentSite?.Id ?? null,
        this.selectedContentService.currentSite?.Name ?? null,
      );

      this.socket.connect();
    } catch (error) {
      console.error('Error refreshing socket access token:', error);
    }
  }

  connect(
    device: string,
    country: string,
    department: string,
    type: "device" | "site",
    serial: string,
    material: string,
    siteId: string,
    siteName: string): void {
    void this.initializeSocketConnection(device, country, department, type, serial, material, siteId, siteName);
  }

  private async initializeSocketConnection(
    device: string,
    country: string,
    department: string,
    type: "device" | "site",
    serial: string,
    material: string,
    siteId: string,
    siteName: string): Promise<void> {
    try {
      const auth = await this.buildSocketAuthPayload(device, country, department, type, serial, material, siteId, siteName);

      this.socket = io(this.getSocketUrl(), {
        transports: ["websocket"],
        auth,
      });

      this.registerSocketHandlers();
    } catch (error) {
      console.error('Error acquiring socket access token:', error);
    }
  }

  //This function is called whenever there is user activity (click, scroll, keypress, etc)
  async checkActivity() {

    if (!this.socket) {
      return;
    }

    //If we are connected to a socket, then we simply reset the timer when user interaction is detected. When the timer runs out, the backend will end the session for us
    if (this.socket.connected) {
      this.socket.emit('checkActivity');
    }

    //When there is no current socket connection, we only want to create a new session if an instrument is selected
    else {
      if (this.selectedContentService.selection.kind !== 'none') {
        await this.reconnectSocket();
      }
    }
  }

  resetSubscriptions() {
    this.socket.emit('resetSubscriptions');
  }

  private handleClick() {

    if (!this.socket || !this.authService.userData.consented) {
      return;
    }

    // console.log('🖱️ Click detected');
    if (this.cooldownActive) {
      // Click ignored due to active cooldown
      return;
    }

    this.cooldownActive = true;
    console.log('🟢 Executing click logic');

    // Place your logic here (runs only once every 80s)
    void this.checkActivity();

    // Start 80-second cooldown
    this.timer = setTimeout(() => {
      this.cooldownActive = false;
      console.log('🕒 Cooldown finished. Ready again.');
    }, 30000); // 30s
  }


  disconnect(): void {
    if (this.socket && this.socket.connected) {
      console.log('❌ Disconnecting from socket');
      this.socket.disconnect();
    }

  }
}